import React from 'react';
import './PlaceSearchResultCard.css';
import Location from '@/models/location';
import CustomImage from '../CustomImage/CustomImage';
import PlusIcon from '../../assets/plus-white.svg';
import Image from '@/components/AppImage/AppImage';
import { getFirstPhotoUrl } from '@/utils/photoUtils';
import { determineSlotType } from '@/utils/slotTypeDeterminer';
import { getPlaceSearchResultSubtitle } from '@/utils/placeSearchResultSubtitle';
import { PlaceSearchRowTypeIcon, type PlaceSearchRowSlotKind } from './PlaceSearchRowTypeIcon';

interface PlaceSearchResultCardProps {
    location: Location;
    onAdd: (location: Location) => void;
    onPreview?: (location: Location) => void;
    isAdding?: boolean;
    isFocused?: boolean;
    selectable?: boolean;
    isSelected?: boolean;
}

function PlaceTypeIcon({ location }: { location: Location }): React.ReactElement {
    const t = determineSlotType(location);
    const kind: PlaceSearchRowSlotKind =
        t === 'eat' || t === 'stay' || t === 'flight' ? t : 'do';
    return <PlaceSearchRowTypeIcon kind={kind} />;
}

/**
 * Single place row for Add-place flows: thumbnail, type icon, name, city/region line, + / multi-select.
 */
const PlaceSearchResultCard: React.FC<PlaceSearchResultCardProps> = ({
    location,
    onAdd,
    onPreview,
    isAdding = false,
    isFocused = false,
    selectable = false,
    isSelected = false,
}) => {
    const placeName = location.name || 'Unknown location';
    const subtitle = getPlaceSearchResultSubtitle(location);
    const imageUrl =
        getFirstPhotoUrl(
            {
                photos: location.photos,
                google_places_data: location.google_places_data,
            },
            400
        ) ||
        location.thumbnailPhotoURI ||
        undefined;

    const recommendationCount = location.rec_count || 0;

    const handleRowClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        if ((e.target as HTMLElement).closest('.place-search-result-row-add')) {
            return;
        }
        // Multi-select / batch flows pass both selectable and onPreview; row must toggle, not open preview.
        if (selectable) {
            onAdd(location);
            return;
        }
        if (onPreview) {
            onPreview(location);
        } else {
            onAdd(location);
        }
    };

    const handleAddButtonClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation();
        onAdd(location);
    };

    return (
        <div
            className={`place-search-result-row ${isFocused ? 'place-search-result-row--focused' : ''}`}
            onClick={handleRowClick}
            role="button"
            tabIndex={isFocused ? 0 : -1}
            aria-label={
                onPreview
                    ? `View details for ${placeName}`
                    : selectable
                      ? isSelected
                          ? `Deselect ${placeName}`
                          : `Select ${placeName}`
                      : `Add ${placeName}`
            }
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (selectable) {
                        onAdd(location);
                    } else if (onPreview) {
                        onPreview(location);
                    } else {
                        onAdd(location);
                    }
                }
            }}
        >
            <div className="place-search-result-row-thumb">
                <CustomImage
                    src={imageUrl}
                    alt=""
                    fill
                    sizes="(max-width: 480px) 48px, 56px"
                    fallbackSrc="/images/default_post.png"
                    lazy
                    style={{ objectFit: 'cover', objectPosition: 'center', borderRadius: 8 }}
                />
                {recommendationCount > 0 ? (
                    <span className="place-search-result-row-thumb-badge">
                        {recommendationCount} {recommendationCount === 1 ? 'rec' : 'recs'}
                    </span>
                ) : null}
            </div>

            <div className="place-search-result-row-body">
                <div className="place-search-result-row-title-line">
                    <span className="place-search-result-row-type-icon">
                        <PlaceTypeIcon location={location} />
                    </span>
                    <h3 className="place-search-result-row-name" title={placeName}>
                        {placeName}
                    </h3>
                </div>
                <p className="place-search-result-row-subtitle" title={subtitle}>
                    {subtitle}
                </p>
            </div>

            <button
                className={`place-search-result-row-add${
                    selectable && isSelected ? ' place-search-result-row-add--selected' : ''
                }`}
                onClick={handleAddButtonClick}
                disabled={isAdding}
                aria-label={
                    selectable
                        ? isSelected
                            ? `Deselect ${placeName}`
                            : `Select ${placeName}`
                        : `Add ${placeName}`
                }
                type="button"
            >
                {isAdding ? (
                    <span className="place-search-result-row-add-loading">…</span>
                ) : selectable && isSelected ? (
                    <span className="place-search-result-row-check" aria-hidden>
                        ✓
                    </span>
                ) : (
                    <Image src={PlusIcon} alt="" width={20} height={20} />
                )}
            </button>
        </div>
    );
};

export default React.memo(PlaceSearchResultCard);
